import { ShieldCheck, FileX, AlertTriangle, CheckCircle, DollarSign, Scale } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const LienRelease = () => {
  const mainContent = (
    <>
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Lien Release and Removal Services in Houston, Texas</h2>
            <p className="text-lg text-muted-foreground mb-8">
              A lien is a legal claim against your property that must be satisfied before you can sell, refinance, or transfer clear title. Liens can arise from unpaid debts, judgments, taxes, contractor bills, HOA dues, or child support. These encumbrances cloud your title and prevent real estate transactions from closing. Our experienced attorneys specialize in identifying, negotiating, and removing liens to clear your property title. Whether you're dealing with a wrongful lien, an outdated mortgage, or legitimate debts that need resolution, we provide comprehensive legal services to restore clear ownership and enable your real estate goals.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertTriangle className="inline h-5 w-5 mr-2 text-accent" />
                Why Lien Removal Matters
              </p>
              <p className="text-muted-foreground">
                Title companies require clear title before issuing title insurance. If liens appear on your property, your transaction won't close, and you can't sell or refinance. Even old or incorrect liens must be formally released. Ignoring liens doesn't make them go away—they accumulate interest and can lead to foreclosure. Professional legal assistance ensures liens are properly resolved and removed from public records.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">Types of Liens We Handle</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <DollarSign className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Tax Liens</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Federal tax liens (IRS)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>State tax liens (Texas Comptroller)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Property tax liens</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Franchise tax board liens</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Scale className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Judgment Liens</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Court judgments from lawsuits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Debt collection judgments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Small claims court judgments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Abstract of judgment filings</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <FileX className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Mechanic's & Construction Liens</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Contractor liens for unpaid work</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Materialmen's liens for supplies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Subcontractor liens</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Design professional liens</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <ShieldCheck className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Other Common Liens</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>HOA liens for unpaid dues/assessments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Child support liens</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Unreleased mortgage liens</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Code enforcement liens</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Our Lien Resolution Services</h3>
            <div className="space-y-6 mb-12">
              <div className="bg-muted/30 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-accent" />
                  Lien Identification & Title Search
                </h4>
                <p className="text-muted-foreground ml-8">
                  Comprehensive search of public records to identify all liens affecting your property, including federal, state, and local databases.
                </p>
              </div>

              <div className="bg-muted/30 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-accent" />
                  Lien Validity Analysis
                </h4>
                <p className="text-muted-foreground ml-8">
                  Review each lien to determine if it was properly filed, is still enforceable, or contains legal defects that allow for challenge or removal.
                </p>
              </div>

              <div className="bg-muted/30 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-accent" />
                  Negotiation & Settlement
                </h4>
                <p className="text-muted-foreground ml-8">
                  Negotiate with lien holders to reduce amounts owed, establish payment plans, or settle for less than the full amount to obtain releases.
                </p>
              </div>

              <div className="bg-muted/30 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-accent" />
                  Wrongful Lien Removal
                </h4>
                <p className="text-muted-foreground ml-8">
                  File legal actions to remove fraudulent, expired, or improperly filed liens through summary judgment or expedited proceedings.
                </p>
              </div>

              <div className="bg-muted/30 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-accent" />
                  Release Documentation & Recording
                </h4>
                <p className="text-muted-foreground ml-8">
                  Ensure proper release of lien affidavits are executed, notarized, and recorded with the county clerk to clear public records.
                </p>
              </div>

              <div className="bg-muted/30 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-accent" />
                  Title Clearance Coordination
                </h4>
                <p className="text-muted-foreground ml-8">
                  Work directly with title companies to ensure all liens are properly satisfied and title insurance can be issued.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Common Lien Issues & Solutions</h2>
            <div className="grid grid-cols-1 gap-6">
              {[
                {
                  issue: "Paid But Not Released",
                  problem: "You paid off a debt or mortgage years ago, but the lien was never formally released from public records.",
                  solution: "We contact the lien holder, obtain a release affidavit, and record it with the county to clear title."
                },
                {
                  issue: "Lien Holder Out of Business",
                  problem: "The company that filed the lien no longer exists, making it difficult to obtain a release.",
                  solution: "We file a motion with the court to release the lien based on evidence of payment or statute of limitations."
                },
                {
                  issue: "Disputed Debt",
                  problem: "You disagree with the validity of the lien or the amount claimed.",
                  solution: "We challenge the lien through legal proceedings, negotiate settlement, or seek court order for removal."
                },
                {
                  issue: "Multiple Liens",
                  problem: "Several liens from different sources cloud your title and exceed your property value.",
                  solution: "We prioritize liens by legal priority, negotiate global settlements, and coordinate releases to clear title."
                },
                {
                  issue: "Expired Liens",
                  problem: "Old liens that should have expired under statute of limitations but remain on record.",
                  solution: "We file for judicial determination that liens are no longer enforceable and obtain court order for removal."
                }
              ].map((item, index) => (
                <div key={index} className="bg-card p-6 rounded-lg border border-accent/20">
                  <h3 className="text-xl font-bold mb-2 text-accent">{item.issue}</h3>
                  <p className="text-muted-foreground mb-2"><span className="font-semibold">Problem:</span> {item.problem}</p>
                  <p className="text-muted-foreground"><span className="font-semibold">Solution:</span> {item.solution}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Our Lien Removal Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <ShieldCheck className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Proven Results</h3>
                <p className="text-muted-foreground">Successfully removed thousands of liens over 20+ years of practice</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Scale className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Legal Expertise</h3>
                <p className="text-muted-foreground">Deep knowledge of Texas lien law and title clearance procedures</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <DollarSign className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Cost-Effective Solutions</h3>
                <p className="text-muted-foreground">Negotiate reduced settlements and avoid unnecessary costs</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Lien Release and Removal"
      subtitle="Clear Your Property Title and Enable Real Estate Transactions"
      icon={<ShieldCheck className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default LienRelease;